import { getItemsSearchedByType } from 'plantItemRenewal/utils/PlantItemRenewalHelpers';
import { PLANT_API_BASE_URL } from 'shared/constants/constants';
import { LoadingStates } from 'shared/constants/loadingStates';
import { getApplication } from 'shared/redux/applicationSlice';

const { createSlice, createAsyncThunk } = require('@reduxjs/toolkit');
const { default: axios } = require('axios');
const { clearPlantItemRenewalForm } = require('plantItemNew/redux/slices/formSlice');

const initialState = {
  status: 'idle',
  searchBy: '',
  renewalNumber: '',
  itemRegistrationNumber: '',
  itemLocationPostcode: '',
  folderRsn: '',
  folderType: '',
  statusCode: '',
  expiryDate: '',
  people: [],
  property: [],
  tooEarlyFlg: '',
  afterGraceFlg: '',
  items: [],
};

const renewalItemsSlice = createSlice({
  name: 'renewalItems',
  initialState,
  reducers: {
    storeRenewalNumber(state, action) {
      state.renewalNumber = action.payload.renewalNumber;
      state.itemRegistrationNumber = '';
      state.itemLocationPostcode = '';
      state.searchBy = action.payload.searchBy;
    },
    storeItemRegistrationNumber(state, action) {
      state.renewalNumber = '';
      state.itemRegistrationNumber = action.payload.itemRegistrationNumber;
      state.itemLocationPostcode = action.payload.itemLocationPostcode;
      state.searchBy = action.payload.searchBy;
    },
    selectForRenewal(state, action) {
      state.items = state.items.map((e) => {
        if (e.tangibleRsn == action.payload) {
          return { ...e, isSelected: true };
        } else {
          return e;
        }
      });
    },
    unselectForRenewal(state, action) {
      state.items = state.items.map((e) => {
        if (e.tangibleRsn == action.payload) {
          return { ...e, isSelected: false };
        } else {
          return e;
        }
      });
    },
    updateCertificate(state, action) {
      state.items = state.items.map((e) => {
        if (e.tangibleRsn == action.payload.tangibleRsn) {
          return { ...e, ...action.payload };
        } else {
          return e;
        }
      });
    },
    updateInfos(state, action) {
      state.items = state.items.map((e) => {
        if (e.tangibleRsn !== action.payload.tangibleRsn) return e;

        const newInfoList = action.payload.tangibleInfos || [];
        let oldInfoList = e.tangibleInfos || [];

        newInfoList.forEach((info) => (info.infoCode = +info.infoCode));

        const newInfoCodeList = newInfoList.map((i) => i.infoCode);

        oldInfoList = oldInfoList.filter((oldInfo) => !newInfoCodeList.includes(oldInfo.infoCode));

        return { ...e, tangibleInfos: [...oldInfoList, ...newInfoList] };
      });
    },
  },
  extraReducers: (builder) => {
    // TODO: Call attachment API to delete if any files were uploaded!
    builder
      .addCase(clearPlantItemRenewalForm, () => {
        return initialState;
      })
      .addCase(validateRenewalOrRego.pending, (state, action) => {
        state.status = LoadingStates.Loading;
        state.folderRsn = '';
      })
      .addCase(validateRenewalOrRego.fulfilled, (state, action) => {
        const payload = action.payload;
        return { ...state, status: LoadingStates.Succeeded, ...payload };
      })
      .addCase(validateGracePeriod.pending, (state, action) => {
        state.status = LoadingStates.Loading;
        state.tooEarlyFlg = '';
        state.afterGraceFlg = '';
      })
      .addCase(validateGracePeriod.fulfilled, (state, action) => {
        state.status = LoadingStates.Succeeded;
        state.tooEarlyFlg = action.payload.tooEarlyFlg;
        state.afterGraceFlg = action.payload.afterGraceFlg;
      })
      .addCase(getTangibleDetails.pending, (state, action) => {
        state.status = LoadingStates.Loading;
        state.items = [];
      })
      .addCase(getTangibleDetails.fulfilled, (state, action) => {
        state.status = LoadingStates.Succeeded;
        state.items = action?.payload?.tangibles || [];
      })
      .addCase(getApplication.fulfilled, (state, action) => {
        return { ...action.payload.applicationData.renewalItems };
      });
  },
});

const baseUrl = `${PLANT_API_BASE_URL}/renewal`;

export const validateRenewalOrRego = createAsyncThunk('renewalItems/validateRenewal', async (_, { getState }) => {
  const renewalItems = getState().renewalItems;

  let { itemRegistrationNumber } = renewalItems;

  // PID-2328: Truncate characters following the second slash.
  if (itemRegistrationNumber) {
    itemRegistrationNumber = itemRegistrationNumber.split('/', 2).join('/').trim();
  }

  const payload = {
    licenseType: 'PIR',
    ...(renewalItems.searchBy === 'renewalNumber' && { renewalNumber: `PIR-${renewalItems.renewalNumber}` }),
    ...(renewalItems.searchBy === 'itemRegistrationNumber' && {
      registrationNumber: itemRegistrationNumber,
      postCode: renewalItems.itemLocationPostcode,
    }),
  };

  const response = await axios.post(`${baseUrl}/validate`, payload, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  return response.data.response;
});

export const validateGracePeriod = createAsyncThunk('renewalItems/validateGracePeriod', async (_, { getState }) => {
  const renewalItems = getState().renewalItems;
  const payload = {
    folderRsn: renewalItems.folderRsn,
    tangibleId: renewalItems.itemRegistrationNumber,
    attributeType: getItemsSearchedByType(renewalItems.searchBy),
  };

  const response = await axios.post(`${baseUrl}/graceperiod`, payload, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  return response.data.response[0];
});

export const getTangibleDetails = createAsyncThunk(
  'renewalItems/tangibleDetails',
  async (_, { getState }) => {
    const renewalItems = getState().renewalItems;
    const payload = {
      folderRsn: renewalItems.folderRsn,
    };

    const response = await axios.post(`${baseUrl}/tangibles`, payload, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    return response?.data?.response;
  },
  {
    condition: (arg, { getState }) => {
      const { renewalItems } = getState();
      return renewalItems.items.length == 0;
    },
  },
);

export const {
  storeRenewalNumber,
  storeItemRegistrationNumber,
  selectForRenewal,
  unselectForRenewal,
  updateCertificate,
  updateInfos,
} = renewalItemsSlice.actions;
export const renewalItemsReducer = renewalItemsSlice.reducer;
